import { useState, useEffect } from 'react';
import { Container, TextField, Button, FormControl, InputLabel, Grid, Paper, Select, MenuItem, SelectChangeEvent } from '@mui/material';
import { getBusinessProfile, updateBusinessProfile, getAreas } from '@services/profileService';
import { toast } from 'react-toastify';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import dayjs, { Dayjs } from 'dayjs';
import AddressAutocomplete from '@components/common/AddressAutocomplete';
import GoogleMapsLinks from '../components/common/GoogleMapsLinks';
import AreaSuggestion from '../components/common/AreaSuggestion';
import { matchPlaceToAreaWithAutoSelect } from '../utils/areaMatchingUtils';
import type { PlaceData as AreaUtilPlaceData, RegionData as AreaUtilRegionData, MatchWithAutoSelectResult } from '../utils/areaMatchingUtils';
import type { AcceptSuggestionPayload, AreaSuggestionMatchResult } from '../components/common/AreaSuggestion';

interface BusinessProfileFormValues {
  email: string;
  phone: string;
  companyName: string;
  businessName: string;
  businessAddress: string;
  businessArea: string;
  businessDescription: string;
  businessHours: {
    startHour: string;
    endHour: string;
  };
  region: string;
  coordinates: [number, number];
  placeId: string;
  [key: string]: unknown;
}

interface AreaLocation {
  _id: string;
  chinese: string;
  english: string;
}

interface AreaRegion {
  _id: string;
  region_chinese: string;
  region_english: string;
  locations: AreaLocation[];
}

const BusinessProfile = () => {
  const { t, i18n } = useTranslation();
  const { control, handleSubmit, setValue } = useForm<BusinessProfileFormValues>({
    defaultValues: {
      email: '',
      phone: '',
      companyName: '',
      businessName: '',
      businessAddress: '',
      businessArea: '',
      businessDescription: '',
      businessHours: {
        startHour: '',
        endHour: '',
      },
      region: '',
      coordinates: [],
      placeId: ''
    }
  });

  const [selectedRegion, setSelectedRegion] = useState<string>('');
  const [availableLocations, setAvailableLocations] = useState<AreaLocation[]>([]);
  const [areaData, setAreaData] = useState<AreaRegion[]>([]);
  const [businessArea, setBusinessArea] = useState<string>('');
  const [currentPlaceId, setCurrentPlaceId] = useState<string>('');
  const [currentAddress, setCurrentAddress] = useState<string>('');
  const [areaSuggestion, setAreaSuggestion] = useState<MatchWithAutoSelectResult | null>(null);

  const handleRegionChange = (event: SelectChangeEvent<string>) => {
    console.log('Region change:', event.target.value);
    const regionId = event.target.value;
    setSelectedRegion(regionId);
    const region = areaData.find((r) => r._id === regionId);
    setAvailableLocations(region ? region.locations : []);
    setValue('region', regionId);
    setValue('businessArea', '');
  };

  const handleLocationChange = (event: SelectChangeEvent<string>) => {
    console.log('Location change:', event.target.value);
    setValue('businessArea', event.target.value);
    setBusinessArea(event.target.value);
  };

  const handleAreaSuggestionAccept = (suggestion: AcceptSuggestionPayload) => {
    console.log('[BusinessProfile] Accepting area suggestion:', suggestion);
    // Set the region and area based on the suggestion
    setSelectedRegion(suggestion.regionId);
    setValue('region', suggestion.regionId, { shouldValidate: true });
    setValue('businessArea', suggestion.locationId, { shouldValidate: true });
    setBusinessArea(suggestion.locationId);

    // Clear the suggestion
    setAreaSuggestion(null);

    // Show success message
    toast.success(t('areaSuggestion.accepted', {
      area: suggestion.match.chinese || suggestion.match.english
    }));
  };

  const handleAreaSuggestionDismiss = () => {
    setAreaSuggestion(null);
  };

  const fetchProfile = async () => {
    try {
      const result = await getBusinessProfile();
      const profile = result;
      Object.keys(profile).forEach((key: string) => {
        if (key !== 'businessArea') {
          (setValue as (name: string, value: unknown) => void)(key, (profile as Record<string, unknown>)[key]);
        }
      });
      setSelectedRegion((profile as Record<string, unknown>).region as string);
      setBusinessArea((profile as Record<string, unknown>).businessArea as string);
    } catch (error) {
      console.error('Error fetching business profile status:', error);
    }
  };

  useEffect(() => {
    const region = areaData.find((r) => r._id === selectedRegion);
    setAvailableLocations(region ? region.locations : []);
    setValue('businessArea', businessArea);
  }, [selectedRegion, areaData, businessArea, setValue]);

  useEffect(() => {
    const fetchAreas = async () => {
      try {
        const areasResult = await getAreas();
        setAreaData(Array.isArray(areasResult) ? areasResult : []);
      } catch (error) {
        console.error('Error fetching areas:', error);
      }
    };

    fetchProfile();
    fetchAreas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async (data: BusinessProfileFormValues) => {
    console.log('Submitting data:', data);
    try {
      const result = await updateBusinessProfile(data as Partial<import('@app-types/models/business').BusinessProfile>);
      const updatedProfile = result;
      Object.keys(updatedProfile).forEach((key: string) => {
        (setValue as (name: string, value: unknown) => void)(key, (updatedProfile as Record<string, unknown>)[key]);
      });
      toast.success(t('businessProfile.profileUpdateSuccess'));
    } catch (error) {
      console.error('Error updating business profile:', error);
      toast.error(t('businessProfile.profileUpdateError'));
    }
  };

  return (
    <Container className="business-profile-container">
      <Paper sx={{ p: 4 }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    margin="normal"
                    label={t('businessProfile.email')}
                    InputProps={{ readOnly: true }}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="phone"
                control={control}
                rules={{
                  pattern: {
                    value: /^\d{8}$/,
                    message: 'Phone number must be exactly 8 digits'
                  }
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    fullWidth
                    margin="normal"
                    label={t('businessProfile.phone')}
                    error={!!error}
                    helperText={error?.message}
                    inputProps={{ maxLength: 8 }}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="companyName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    margin="normal"
                    label={t('businessProfile.companyName')}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Controller
                name="businessAddress"
                control={control}
                render={({ field }) => (
                  <AddressAutocomplete
                    label={t('businessProfile.businessAddress')}
                    value={field.value}
                    onInputChange={(value: string) => {
                      field.onChange(value);
                    }}
                    onPlaceSelected={(placeData) => {
                      console.log('[BusinessProfile] onPlaceSelected called with:', placeData);
                      // placeData structure: { address, coordinates, place_id }
                      // coordinates is an array [lng, lat] for GeoJSON format
                      if (placeData.coordinates && Array.isArray(placeData.coordinates) && placeData.coordinates.length === 2) {
                        setValue('coordinates', placeData.coordinates as [number, number], { shouldValidate: true });
                      }
                      // Store the place_id for generating Google Maps links
                      if (placeData.place_id) {
                        setValue('placeId', placeData.place_id, { shouldValidate: true });
                        setCurrentPlaceId(placeData.place_id);
                      }
                      // Also update the address field
                      field.onChange(placeData.address);
                      setCurrentAddress(placeData.address);

                      // Perform area matching
                      if (areaData.length > 0) {
                        const matchResult = matchPlaceToAreaWithAutoSelect(placeData as AreaUtilPlaceData, areaData as AreaUtilRegionData[]);
                        console.log('[BusinessProfile] Area match result:', matchResult);

                        if (matchResult.shouldAutoSelect && matchResult.match) {
                          // Auto-select the best match
                          const { match } = matchResult;
                          console.log('[BusinessProfile] Auto-selecting area:', match);
                          setSelectedRegion(match.region_id);
                          setValue('region', match.region_id, { shouldValidate: true });
                          setValue('businessArea', match.location_id, { shouldValidate: true });
                          setBusinessArea(match.location_id);
                          toast.success(t('areaSuggestion.autoSelected', {
                            area: match.chinese || match.english,
                            confidence: matchResult.confidence
                          }));
                        } else if (matchResult.shouldShowSuggestion) {
                          // Show manual suggestion for lower confidence matches
                          setAreaSuggestion(matchResult);
                        }
                      }
                    }}
                  />
                )}
              />
              {/* Google Maps Links */}
              {currentPlaceId && currentAddress && (
                <GoogleMapsLinks placeId={currentPlaceId} address={currentAddress} />
              )}

              {/* Area Suggestion */}
              {areaSuggestion && areaSuggestion.shouldShowSuggestion && (
                <AreaSuggestion
                  matchResult={areaSuggestion as AreaSuggestionMatchResult}
                  onAcceptSuggestion={handleAreaSuggestionAccept}
                  onDismiss={handleAreaSuggestionDismiss}
                  currentSelection={businessArea}
                  showDebugInfo={import.meta.env.DEV}
                />
              )}
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="region"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth margin="normal">
                    <InputLabel>{t('businessProfile.region')}</InputLabel>
                    <Select
                      {...field}
                      label={t('businessProfile.region')}
                      value={field.value || ''}
                      onChange={(event) => {
                        field.onChange(event);
                        handleRegionChange(event as SelectChangeEvent<string>);
                      }}
                    >
                      {areaData.map((region) => (
                        <MenuItem key={region._id} value={region._id}>
                          {i18n.language === 'zh' ? region.region_chinese : region.region_english}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="businessArea"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth margin="normal">
                    <InputLabel>{t('businessProfile.businessArea')}</InputLabel>
                    <Select
                      {...field}
                      value={field.value || ''}
                      onChange={
                        (event) => {
                          field.onChange(event);
                          handleLocationChange(event as SelectChangeEvent<string>);
                        }
                      }
                      label={t('businessProfile.businessArea')}
                      disabled={!selectedRegion}
                    >
                      {availableLocations.map((location) => (
                        <MenuItem key={location._id} value={location._id}>
                          {i18n.language === 'zh' ? location.chinese : location.english}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Controller
                name="businessDescription"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    margin="normal"
                    label={t('businessProfile.businessDescription')}
                    multiline
                    rows={4}
                  />
                )}
              />
            </Grid>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                  name="businessHours.startHour"
                  control={control}
                  render={({ field }) => {
                    const formatTime = (time: string): Dayjs | null => {
                      if (!time) return null;
                      const [hours, minutes] = time.split(':');
                      return dayjs().hour(parseInt(hours)).minute(parseInt(minutes));
                    };

                    return (
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <TimePicker
                          label={t('businessProfile.startHour')}
                          value={formatTime(field.value)}
                          onChange={(newValue: Dayjs | null) => {
                            if (!newValue || !dayjs.isDayjs(newValue)) {
                              field.onChange('');
                              return;
                            }
                            const hours = String(newValue.hour()).padStart(2, '0');
                            const minutes = String(newValue.minute()).padStart(2, '0');
                            field.onChange(`${hours}:${minutes}`);
                          }}
                          slotProps={{ textField: { fullWidth: true, margin: "normal" } }}
                        />
                      </LocalizationProvider>
                    );
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                  name="businessHours.endHour"
                  control={control}
                  render={({ field }) => {
                    const formatTime = (time: string): Dayjs | null => {
                      if (!time) return null;
                      const [hours, minutes] = time.split(':');
                      return dayjs().hour(parseInt(hours)).minute(parseInt(minutes));
                    };

                    return (
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <TimePicker
                          label={t('businessProfile.endHour')}
                          value={formatTime(field.value)}
                          onChange={(newValue: Dayjs | null) => {
                            if (!newValue || !dayjs.isDayjs(newValue)) {
                              field.onChange('');
                              return;
                            }
                            const hours = String(newValue.hour()).padStart(2, '0');
                            const minutes = String(newValue.minute()).padStart(2, '0');
                            field.onChange(`${hours}:${minutes}`);
                          }}
                          slotProps={{ textField: { fullWidth: true, margin: "normal" } }}
                        />
                      </LocalizationProvider>
                    );
                  }}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid size={{ xs: 12 }}>
              <Button
                variant="contained"
                color="primary"
                type="submit"
              >
                {t('businessProfile.saveChanges')}
              </Button>
            </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default BusinessProfile;
